import { Injectable, inject, NgZone } from "@angular/core";
import {
	Auth,
	signInWithEmailAndPassword,
	UserCredential,
	User as FirebaseUser,
	getAuth,
	signInWithEmailLink,
	isSignInWithEmailLink,
} from "@angular/fire/auth";
import { Observable, throwError, of, from, BehaviorSubject } from "rxjs";
import { environment } from "../../environments/environment";
import {
	Firestore,
	doc,
	updateDoc,
	serverTimestamp,
	collection,
	addDoc,
	Timestamp,
	getDoc,
} from "@angular/fire/firestore";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { tap, catchError, map, switchMap } from "rxjs/operators";
import { Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { RecaptchaFailureReason, RecaptchaService } from "./recaptcha.service";

/**
 * The Functions SDK reports a request that never reached the server (blocked by a
 * firewall, offline) as one of these codes, with the bare code as its message.
 * Errors thrown by our functions always carry a real message.
 */
const UNREACHABLE_CODES = ["internal", "unavailable", "deadline-exceeded"];

export const UNREACHABLE_MESSAGE =
	"We couldn't reach the Zanga service from this network. A firewall or web filter may be blocking it. " +
	"Please try a different network (for example your phone on mobile data), or use the link in your survey invitation email.";

function isUnreachable(error: any): boolean {
	const code = String(error?.code ?? "").replace(/^functions\//, "");
	return UNREACHABLE_CODES.includes(code) && error?.message === code;
}

@Injectable({
	providedIn: "root",
})
export class AuthService {
	private auth: Auth = inject(Auth);
	private zone: NgZone = inject(NgZone);
	private firestore: Firestore = inject(Firestore);
	private functions: Functions = inject(Functions);
	private recaptchaService = inject(RecaptchaService);
	public user$: Observable<FirebaseUser | null>;
	public userDoc$ = new BehaviorSubject<any | null>(null);

	constructor(
		private router: Router,
		private snackBar: MatSnackBar,
	) {
		// Listen for auth state changes
		this.user$ = new Observable((subscriber) => {
			return this.auth.onAuthStateChanged(
				(user) => subscriber.next(user),
				(error) => subscriber.error(error),
				() => subscriber.complete(),
			);
		});

		this.user$.subscribe(async (user) => {
			// If a user signed in, update their lastSignInAt field
			if (user) {
				const userDoc = await this.fetchUserDocument(user.uid || "");
				if (userDoc) {
					this.userDoc$.next(userDoc);
				}
				// Add a slight delay to ensure Firebase Auth is fully initialized
				setTimeout(() => {
					this.updateUserLastSignIn(user.uid);
				}, 500);
			} else {
				this.userDoc$.next(null);
			}
		});
	}

	// Helper method to fetch user document from Firestore with error handling
	private async fetchUserDocument(uid: string): Promise<any | null> {
		try {
			const userDocData = await getDoc(doc(this.firestore, "users", uid));
			if (!userDocData.exists()) {
				console.warn(`User document not found for uid: ${uid}`);
				return null;
			}
			
			const userDoc = userDocData.data() as any;
			if (userDoc) {
				// ZAN-291: user docs no longer carry a roles field. The members
				// app never gates on roles, so we don't synthesise one.
				userDoc.uid = uid;
				return userDoc;
			}
			return null;
		} catch (error) {
			console.error("Error fetching user document:", error);
			return null;
		}
	}

	// Refresh the user document from Firestore
	async refreshUserDoc(): Promise<void> {
		const user = this.auth.currentUser;
		if (user) {
			const userDoc = await this.fetchUserDocument(user.uid);
			if (userDoc) {
				this.userDoc$.next(userDoc);
			}
		}
	}

	signIn(email: string, password: string): Observable<UserCredential> {
		return new Observable<UserCredential>((observer) => {
			this.zone.runOutsideAngular(() => {
				signInWithEmailAndPassword(this.auth, email, password)
					.then((result) => {
						this.zone.run(() => {
							observer.next(result);
							observer.complete();
						});
					})
					.catch((error) => {
						this.zone.run(() => {
							console.error("Sign in error:", error);
							let errorMessage = "An unknown error occurred";

							switch (error.code) {
								case "auth/invalid-email":
									errorMessage = "Invalid email format";
									break;
								case "auth/user-disabled":
									errorMessage = "This account has been disabled";
									break;
								case "auth/user-not-found":
								case "auth/wrong-password":
									errorMessage = "Invalid email or password";
									break;
								case "auth/too-many-requests":
									errorMessage =
										"Too many unsuccessful login attempts. Please try again later";
									break;
							}

							observer.error(new Error(errorMessage));
						});
					});
			});
		});
	}

	// Get the current authenticated user
	getCurrentUser(): FirebaseUser | null {
		return this.auth.currentUser;
	}

	// Alias for signIn for backward compatibility
	login(email: string, password: string): Observable<UserCredential> {
		return this.signIn(email, password);
	}

	// Sign out the current user
	signOut(): Promise<void> {
		return this.auth.signOut().catch((error) => {
			console.error("Error signing out:", error);
			throw error;
		});
	}

	// Helper method to retry token refresh with exponential backoff
	private async retryTokenRefresh(user: FirebaseUser, maxRetries: number = 3): Promise<boolean> {
		for (let attempt = 1; attempt <= maxRetries; attempt++) {
			try {
				// Force a token refresh to check if it's working
				await user.getIdToken(true);
				// Only log success in development mode, and only if it required retries
				if (!environment.production && attempt > 1) {
					console.log(`Token refresh succeeded on attempt ${attempt}`);
				}
				return true;
			} catch (error: any) {
				// Always log errors, but with less detail in production
				if (!environment.production) {
					console.error(`Token refresh attempt ${attempt}/${maxRetries} failed:`, {
						code: error?.code,
						message: error?.message,
						attempt,
						error
					});
				} else {
					console.error(`Token refresh failed (${error?.code || 'unknown'})`);
				}

				// Check if this is an auth error (not a network error)
				const isAuthError = error?.code === 'auth/invalid-user-token' ||
					error?.code === 'auth/user-token-expired' ||
					error?.message?.includes('403') ||
					error?.message?.includes('Forbidden');

				// If it's an auth error and we've exhausted retries, return false
				if (isAuthError && attempt === maxRetries) {
					return false;
				}

				// If it's a network error, retry with exponential backoff
				if (!isAuthError && attempt < maxRetries) {
					const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
					if (!environment.production) {
						console.log(`Retrying in ${delay}ms...`);
					}
					await new Promise(resolve => setTimeout(resolve, delay));
				} else if (isAuthError) {
					// For auth errors, fail immediately
					return false;
				}
			}
		}
		return false;
	}

	// Check session validity before navigation
	// Returns true if session is valid, false if invalid (and triggers logout)
	async checkSessionBeforeNavigation(): Promise<boolean> {
		const user = this.auth.currentUser;
		if (!user) {
			return false;
		}

		const refreshSucceeded = await this.retryTokenRefresh(user, 2);
		if (!refreshSucceeded) {
			await this.zone.run(async () => {
				this.snackBar.open('Your session has expired. Please login again.', 'Close', {
					duration: 5000,
				});
				await this.signOut();
				this.router.navigate(['/login']);
			});
			return false;
		}
		return true;
	}

	// Send sign-in link to email
	sendSignInLink(email: string, recaptchaToken: string): Observable<void> {
		// Validate the reCAPTCHA token
		if (!recaptchaToken) {
			return throwError(() => new Error("reCAPTCHA verification is required"));
		}

		// Store the email locally so you don't need to ask the user for it again
		// if they open the link on the same device.
		window.localStorage.setItem("emailForSignIn", email);

		// Call the cloud function to request sign-in link
		// The function handles user lookup, display_name construction, and creates pending_email_links
		const requestSignInLinkFn = httpsCallable(this.functions, "requestSignInLink");

		return from(
			requestSignInLinkFn({
				email: email,
				recaptchaToken: recaptchaToken,
				frontend: "members",
			}),
		).pipe(
			map(() => undefined), // Convert to void
			catchError((error) => {
				console.error("Error requesting sign-in link:", error);
				let errorMessage = "An error occurred while requesting the sign-in link";

				if (error.code === "functions/permission-denied") {
					errorMessage = "You don't have permission to perform this action";
				} else if (isUnreachable(error)) {
					errorMessage = UNREACHABLE_MESSAGE;
				} else if (error.message) {
					errorMessage = error.message;
				}

				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Tell the backend that reCAPTCHA couldn't run in this browser, so these failures show
	 * up in the logs. Best effort: the user already sees an error, and this call may be
	 * blocked by the same network filter.
	 */
	reportRecaptchaFailure(email: string, reason: RecaptchaFailureReason): void {
		const requestSignInLinkFn = httpsCallable(this.functions, "requestSignInLink");
		requestSignInLinkFn({ email, recaptchaFailure: reason, frontend: "members" }).catch((error) => {
			console.warn("Could not report reCAPTCHA failure:", error);
		});
	}

	// Update the user's lastSignInAt field in Firestore
	private updateUserLastSignIn(uid: string): void {
		try {
			// First, check if the user document exists
			const userRef = doc(this.firestore, `users/${uid}`);

			// Create an object with the fields to update
			const updateData = {
				last_sign_in_at: serverTimestamp(),
				email_verified: this.auth.currentUser?.emailVerified || false,
			};

			// Update the document
			updateDoc(userRef, updateData).catch((error) => {
				console.error(`Error updating lastSignInAt for user:`, error);
			});
		} catch (error) {
			console.error(`Exception in updateUserLastSignIn:`, error);
		}
	}

	// Check if current URL is a sign-in link
	isEmailSignInLink(url: string): boolean {
		const auth = getAuth();
		return isSignInWithEmailLink(auth, url);
	}

	// Complete sign-in with email link
	completeEmailLinkSignIn(email: string, url: string): Observable<FirebaseUser> {
		const auth = getAuth();
		return from(signInWithEmailLink(auth, email, url)).pipe(
			map((result) => result.user as FirebaseUser),
			tap((user) => {
				// Clear the stored email
				window.localStorage.removeItem("emailForSignIn");

				// Show success message
				this.snackBar.open("Successfully signed in!", "Close", {
					duration: 3000,
				});
			}),
		);
	}

	// Get state from URL
	getSignInLinkState(url: string): any {
		try {
			const params = new URLSearchParams(new URL(url).search);
			const stateStr = params.get("state");
			return stateStr ? JSON.parse(decodeURIComponent(stateStr)) : null;
		} catch (e) {
			console.error("Error parsing sign-in link state:", e);
			return null;
		}
	}

	// Handle email link sign-in flow
	handleEmailLinkSignIn(url: string): Observable<{ user: FirebaseUser | null; isNewUser: boolean }> {
		const email = window.localStorage.getItem("emailForSignIn");

		if (!email) {
			// Navigate to the wrong device component instead of showing a snackbar
			this.router.navigate(["/auth/wrong-device"]);
			return of({ user: null, isNewUser: false });
		}

		return this.completeEmailLinkSignIn(email, url).pipe(
			switchMap((user) => {
				const state = this.getSignInLinkState(url);
				const isNewUser = state?.is_new_user || false;

				if (isNewUser) {
					// Handle new user onboarding
					this.router.navigate(["/welcome"]);
				} else {
					// Redirect to dashboard or home
					this.router.navigate(["/dashboard"]);
				}

				return of({ user, isNewUser });
			}),
		);
	}

	// Sign out the current user and navigate to login
	async logout(): Promise<void> {
		try {
			await this.signOut();
			this.router.navigate(["/login"]);
		} catch (error) {
			console.error("Error during logout:", error);
		}
	}
}
